import React from "react";
import { ChevronRight, Circle } from "lucide-react";

const WhatWeDo = () => {
  return (
    <section className="relative py-14 px-3 sm:px-4 lg:px-6 max-w-7xl mx-auto overflow-hidden">
      {/* Background Cross Pattern - adjusted for transparency */}
      <div
        className="absolute inset-0 -z-10 opacity-[0.2]"
        style={{
          backgroundImage: `linear-gradient(#cbd5e1 1px, transparent 1px), linear-gradient(90deg, #cbd5e1 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
          maskImage:
            "radial-gradient(circle at center, black, transparent 90%)",
        }}
      />

      <div className="grid grid-cols-2 lg:grid-cols-12 gap-4 lg:gap-6 lg:auto-rows-[280px]">
        {/* 1. Main Feature Card - AI Powered Smile Simulation */}
        <div className="col-span-2 aspect-square lg:aspect-auto lg:col-span-4 lg:row-span-2 bg-gradient-to-br from-[#eaf4ff] to-white rounded-[24px] lg:rounded-[32px] p-6 lg:p-8 shadow-sm border border-slate-100 flex flex-col relative overflow-hidden group">
          {/* Subtle Blue Glow / Haze behind icon and grid */}
          <div className="absolute -top-20 -left-20 w-[400px] h-[400px] bg-[#c3e0ff]/30 blur-[70px] rounded-full pointer-events-none z-0" />

          {/* Grid Pattern with mask - fading out */}
          <div
            className="absolute inset-0 opacity-[0.4] pointer-events-none z-0"
            style={{
              backgroundImage: `linear-gradient(#bcd5f5 1px, transparent 1px), linear-gradient(90deg, #bcd5f5 1px, transparent 1px)`,
              backgroundSize: "26px 26px",
              maskImage: "linear-gradient(135deg, black 15%, transparent 60%)",
            }}
          />

          {/* Large Tooth Icon Box in Background - White, Top Right */}
          <div className="absolute -top-2 -right-2 w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 opacity-100 pointer-events-none transition-all duration-700 z-0">
            <img
              src="/icon.webp"
              alt=""
              width="128"
              height="128"
              loading="lazy"
              className="w-full h-full object-contain brightness-0 invert drop-shadow-md"
            />
          </div>

          <div className="z-10">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-2xl flex items-center justify-center mb-3 shadow-lg shadow-blue-500/15">
              <svg
                viewBox="0 0 24 24"
                className="w-6 sm:w-7 h-6 sm:h-7 drop-shadow-[0_0_12px_rgba(37,99,235,0.4)]"
                fill="url(#grad1)"
                stroke="url(#grad1)"
                strokeWidth={2.5}
              >
                <defs>
                  <radialGradient id="grad1" cx="50%" cy="0%" r="100%">
                    <stop offset="0%" stopColor="#60a5fa" />
                    <stop offset="100%" stopColor="#0055ff" />
                  </radialGradient>
                </defs>

                {/* User icon path (from lucide or heroicons) */}
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>

            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-3 sm:mb-6 leading-tight">
              AI-Powered Dental Smile Simulation
            </h3>

            <p className="text-slate-500 text-xs sm:text-[13px] leading-relaxed font-medium relative z-10 w-1/2">
              Photorealistic smile previews that help dental clinics, cosmetic
              dentists, and DSOs boost case acceptance instantly
            </p>
          </div>

          {/* Image handling - using elderly_man.png - Flipped and Rotated to fill bottom right */}
          <div className="absolute left-28 md:right-0 lg:left-0 -bottom-24 lg:-bottom-16 w-[115%] h-[100%] pointer-events-none">
            <img
              src="/elderly_man.webp"
              alt="Smiling elderly man"
              width="800"
              height="800"
              loading="lazy"
              className="w-full h-full object-contain object-right-bottom"
              style={{
                transform: "scaleX(-1) scale(1) rotate(-20deg)",
                transformOrigin: "bottom center",
              }}
            />
          </div>
        </div>

        {/* 2. Secure & Compliant Card */}
        <div className="col-span-2 min-h-[160px] lg:min-h-0 lg:col-span-5 lg:row-span-1 bg-white rounded-[24px] lg:rounded-[32px] p-4 lg:p-4 xl:p-6 shadow-sm border border-slate-100 flex gap-4 lg:gap-3 xl:gap-6 items-center">
          <div className="w-[120px] sm:w-[120px] lg:w-[120px] xl:w-[180px] h-full bg-[#f6f9fc] rounded-[20px] lg:rounded-[24px] p-4 lg:p-3 xl:p-6 flex flex-col items-center justify-center relative overflow-hidden shrink-0">
            <div
              className="absolute inset-0 opacity-[0.35]"
              style={{
                backgroundImage:
                  "linear-gradient(#b8d2f5 1px, transparent 1px), linear-gradient(90deg, #b8d2f5 1px, transparent 1px)",
                backgroundSize: "18px 18px",
                backgroundPosition: "center top",
              }}
            />

            <svg
              viewBox="0 0 48 59"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-12 h-auto drop-shadow-[0_8px_12px_rgba(37,99,235,0.3)] mb-5 relative z-10"
            >
              <path
                d="M24 0L48 10.6667V26.6667C48 41.4933 37.7067 55.1467 24 58.6667C10.2933 55.1467 0 41.4933 0 26.6667V10.6667L24 0Z"
                fill="url(#shield_grad)"
              />
              <path
                d="M20.5333 38.4L10.6667 28.5333L14.4267 24.7733L20.5333 30.8533L35.2 16.1867L38.96 20L20.5333 38.4Z"
                fill="white"
              />
              <defs>
                <linearGradient
                  id="shield_grad"
                  x1="24"
                  y1="0"
                  x2="24"
                  y2="58.6667"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#3B82F6" />
                  <stop offset="1" stopColor="#1D4ED8" />
                </linearGradient>
              </defs>
            </svg>

            <div className="relative z-10 text-[32px] sm:text-[36px] lg:text-[28px] xl:text-[36px] leading-none font-bold text-slate-900 tracking-tight mb-2">
              98.7%
            </div>
            <div className="relative z-10 text-[11px] sm:text-[13px] lg:text-[11px] xl:text-[14px] font-medium text-slate-700 text-center leading-snug">
              AI Smile Prediction
              <br />
              Accuracy
            </div>

            {/* Soft inner glow/border simulation */}
            <div className="absolute inset-0 rounded-[24px] shadow-[inset_0_0_0_2px_rgba(255,255,255,0.7)] pointer-events-none" />
          </div>

          <div className="flex-1 flex flex-col justify-between py-2 pr-1 xl:pr-2 min-w-0 overflow-hidden">
            <div>
              <h4 className="text-[20px] sm:text-[22px] lg:text-[17px] xl:text-[22px] font-bold text-slate-900 mb-1 lg:mb-1 tracking-tight leading-tight">
                HIPAA-Compliant Smile Simulation
              </h4>
              <p className="text-[#5b6b80] text-[14px] lg:text-[12px] xl:text-[14px] leading-relaxed mt-1.5 lg:mt-1 mb-3 lg:mb-0 lg:line-clamp-3 xl:line-clamp-none">
                Patient data is encrypted with medical-grade security.
                SmileReveal is built with a HIPAA-compliant, privacy-first
                architecture trusted by dental professionals across the U.S.
              </p>
            </div>
            <button className="group flex items-center gap-2 text-white bg-black rounded-full px-4 lg:px-3 xl:px-5 py-2 lg:py-2 xl:py-2.5 text-[12px] lg:text-[11px] xl:text-[13px] font-semibold w-fit hover:bg-slate-800 transition-all hover:gap-3 shadow-md shadow-black/10 shrink-0 mt-2 lg:mt-2">
              Learn More
              <div className="flex items-center text-current">
                <ChevronRight className="h-4 w-4 opacity-40 group-hover:translate-x-0.5 transition-transform" />
                <ChevronRight className="h-5 w-5 opacity-70 -ml-2.5 group-hover:translate-x-0.5 transition-transform delay-75" />
                <ChevronRight className="h-6 w-6 opacity-100 -ml-3.5 group-hover:translate-x-0.5 transition-transform delay-150" />
              </div>
            </button>
          </div>
        </div>

        {/* 3. Trusted Doctors Card */}
        <div className="col-span-1 aspect-square lg:aspect-auto lg:col-span-3 lg:row-span-1 bg-white rounded-[24px] lg:rounded-[32px] p-5 lg:p-8 shadow-sm border border-slate-100 flex flex-col justify-between">
          <div className="flex items-center gap-4">
            <div className="flex -space-x-3 lg:-space-x-4">
              <img
                src="/young_docter.webp"
                width="48"
                height="48"
                loading="lazy"
                className="w-9 h-9 lg:w-12 lg:h-12 rounded-full border-4 border-white shadow-sm object-cover"
                alt="Doctor"
              />
              <img
                src="/middle_aged.webp"
                width="48"
                height="48"
                loading="lazy"
                className="w-9 h-9 lg:w-12 lg:h-12 rounded-full border-4 border-white shadow-sm object-cover"
                alt="Doctor"
              />
              <img
                src="/young_docter.webp"
                width="48"
                height="48"
                loading="lazy"
                className="w-9 h-9 lg:w-12 lg:h-12 rounded-full border-4 border-white shadow-sm object-cover"
                alt="Doctor"
              />
              <div className="w-9 h-9 lg:w-12 lg:h-12 rounded-full border-4 border-white bg-slate-50 flex items-center justify-center text-[10px] lg:text-xs font-bold text-slate-600 shadow-sm">
                40+
              </div>
            </div>
          </div>
          <div>
            <div className="text-4xl lg:text-5xl font-bold text-slate-900 leading-none tracking-tight">
              2M+
            </div>
            <div className="text-[10px] lg:text-xs text-slate-400 font-bold tracking-[0.15em] lg:tracking-[0.2em] mt-2 lg:mt-3 uppercase">
              SMILE SIMULATIONS GENERATED
            </div>
          </div>
        </div>

        {/* 4. Return Customers Card */}
        <div className="col-span-1 aspect-square lg:aspect-auto lg:col-span-3 lg:row-span-1 bg-[#0066ff] rounded-[24px] lg:rounded-[32px] p-5 lg:p-8 shadow-2xl shadow-blue-500/20 flex flex-col justify-between text-white relative overflow-hidden group">
          {/* Subtle Shine effect */}
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-tr from-white/0 via-white/5 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

          <div className="flex -space-x-3 lg:-space-x-4">
            <img
              src="/young_man.webp"
              width="48"
              height="48"
              loading="lazy"
              className="w-9 h-9 lg:w-12 lg:h-12 rounded-full border-4 border-white/20 object-cover shadow-lg"
              alt="User"
            />
            <img
              src="/middle_aged.webp"
              width="48"
              height="48"
              loading="lazy"
              className="w-9 h-9 lg:w-12 lg:h-12 rounded-full border-4 border-white/20 object-cover shadow-lg"
              alt="User"
            />
            <img
              src="/young_woman.webp"
              width="48"
              height="48"
              loading="lazy"
              className="w-9 h-9 lg:w-12 lg:h-12 rounded-full border-4 border-white/20 object-cover shadow-lg"
              alt="User"
            />
            <div className="w-9 h-9 lg:w-12 lg:h-12 rounded-full border-4 border-white/20 bg-white/10 flex items-center justify-center text-[10px] lg:text-xs font-bold backdrop-blur-sm">
              40+
            </div>
          </div>
          <div>
            <div className="text-4xl lg:text-5xl font-bold leading-none tracking-tight">
              95%
            </div>
            <div className="text-[10px] lg:text-xs text-white/70 font-bold tracking-[0.15em] lg:tracking-[0.2em] mt-2 lg:mt-3 uppercase">
              CUSTOMER RETENTION RATE
            </div>
          </div>
        </div>

        {/* 5. Track Patient Engagement & Performance (Custom Graph) */}
        <div className="col-span-2 min-h-[180px] lg:min-h-0 lg:col-span-5 lg:row-span-1 bg-white rounded-[24px] lg:rounded-[32px] p-5 lg:p-5 xl:p-8 shadow-sm border border-slate-100 flex gap-4 lg:gap-4 xl:gap-6 items-center">
          <div className="flex-1 flex flex-col justify-between h-full py-2">
            <div className="w-14 h-14 rounded-full border-[6px] border-blue-600 border-r-transparent flex items-center justify-center relative transform -rotate-45">
              <div className="w-full h-full p-2.5 rotate-45">
                <Circle className="text-blue-600 fill-blue-600 w-full h-full opacity-20" />
              </div>
              <div className="absolute inset-0 flex items-center justify-center rotate-45">
                <div className="w-2 h-2 bg-blue-600 rounded-full" />
              </div>
            </div>
            <h4 className="text-lg md:text-2xl lg:text-[15px] xl:text-xl font-bold text-slate-800 leading-tight pr-2 lg:pr-1 xl:pr-4">
              Track Case Acceptance & Patient Engagement Analytics
            </h4>
          </div>

          {/* Custom CSS/SVG Graph Section */}
          <div className="flex-1 bg-slate-50/50 rounded-3xl p-6 border border-slate-100 relative overflow-hidden h-full">
            {/* Grid Background */}
            <div
              className="absolute inset-0 opacity-[0.25]"
              style={{
                backgroundImage:
                  "linear-gradient(#3b82f6 1px, transparent 1px), linear-gradient(90deg, #3b82f6 1px, transparent 1px)",
                backgroundSize: "16px 16px",
              }}
            />

            {/* Labels */}
            <div className="absolute top-3 left-3 flex gap-1.5 items-center bg-blue-600 text-[9px] font-bold text-white px-2.5 py-1 rounded-full z-10 shadow-lg shadow-blue-500/20">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />{" "}
              HIPAA-Ready
            </div>
            <div className="absolute top-10 right-3 flex gap-1.5 items-center bg-blue-50 text-[9px] font-bold text-blue-600 px-2.5 py-1 rounded-full border border-blue-200 z-10 shadow-sm">
              <span className="w-1.5 h-1.5 bg-blue-600 rounded-full" /> AI
              Engine 3.0
            </div>

            {/* Bars Container */}
            <div className="h-full w-full flex items-end justify-between gap-2.5 pt-12 relative z-0">
              <div className="flex-1 bg-blue-100 rounded-xl h-[40%] transition-all duration-700 ease-out hover:bg-blue-200" />
              <div className="flex-1 bg-blue-200 rounded-xl h-[70%] transition-all duration-700 ease-out hover:bg-blue-300" />
              <div className="flex-1 bg-blue-100 rounded-xl h-[55%] transition-all duration-700 ease-out hover:bg-blue-200" />
              <div className="flex-1 bg-blue-600 rounded-xl h-[90%] relative group shadow-lg shadow-blue-500/30">
                <div className="absolute -top-12 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:-translate-y-1">
                  <div className="p-1 bg-white rounded-full shadow-xl border border-blue-100">
                    <img
                      src="/young_docter.webp"
                      width="32"
                      height="32"
                      loading="lazy"
                      className="w-8 h-8 rounded-full object-cover"
                      alt="profile"
                    />
                  </div>
                  <div className="w-0.5 h-3 bg-blue-600 mx-auto" />
                </div>
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-2 h-2 bg-blue-600 rounded-full border-2 border-white shadow-sm" />
              </div>
              <div className="flex-1 bg-blue-200 rounded-xl h-[50%] transition-all duration-700 ease-out hover:bg-blue-300" />
              <div className="flex-1 bg-blue-100 rounded-xl h-[65%] transition-all duration-700 ease-out hover:bg-blue-200" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatWeDo;
